/**
 * LCA Module — top-level hub.
 *
 * Stakeholder decision (2026-05-11): LCA is promoted from a card inside the
 * Sustainability Hub to its own top-level menu item. This page is the entry
 * point and holds the sub-navigation.
 *
 * Sub-sections:
 *   - Corporate View    — tenant-wide rollup, project list, create-project
 *                         (CorporateLCADashboard, same component as before)
 *   - LCA Master Data   — emission-factor catalog + product-aware filtering
 *                         (DatasetLibrary in corporateMode)
 *
 * Project workspace (Phase 1–4) is opened by clicking a project inside
 * Corporate View — drill-down, not a sibling tab.
 */
import React, { useState } from 'react';
import { BarChart3, Database, Recycle } from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import CorporateLCADashboard from '../sustainability/corporate/CorporateLCADashboard';
import DatasetLibrary from '../../components/lca/DatasetLibrary';

const SUB_SECTIONS = [
  { id: 'corporate', label: 'Corporate View', icon: BarChart3 },
  { id: 'datasets', label: 'LCA Master Data', icon: Database },
];

export default function LCAHub({ user, onLogout }) {
  const [active, setActive] = useState('corporate');

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <Recycle className="w-8 h-8 text-teal-600" />
              <h1 className="text-3xl font-bold text-gray-900">Life Cycle Assessment</h1>
            </div>
            <p className="text-gray-600 mt-1 ml-11">
              ISO 14040/14044 compliant LCA across all sites and reporting periods.
            </p>
          </div>
        </div>

        {/* Sub-navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {SUB_SECTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              className={`px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
                active === id
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Active sub-section */}
        {active === 'corporate' && <CorporateLCADashboard />}
        {active === 'datasets' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden -mx-4 sm:-mx-6 lg:-mx-8">
            <DatasetLibrary
              corporateMode
              onBack={() => setActive('corporate')}
            />
          </div>
        )}
      </div>
    </div>
  );
}
